import { CommissionStatuses } from 'constants/commissionStatuses';
import { Resources } from 'constants/resources';

import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useGoBack } from 'hooks/useGoBack';
import { COMMISSIONS_SCHEMA } from 'pages/Commissions/schemas/schema';
import { transform } from 'pages/Commissions/utils/transform';
import { NotFound } from 'pages/NotFound';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { HttpError, SimpleForm, useEditController, useNotify, useUpdate } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { EditPage } from 'shared/react-admin/Pages/EditPage/EditPage';
import { getSubmissionErrors } from 'utils/getSubmissionErrors';

import { CommissionsEditForm, CommissionsEditFormValues } from './CommissionsEditForm';

export const CommissionsEdit: React.FC = () => {
  const translate = useTypedTranslate();
  const { goBack } = useGoBack();
  const notify = useNotify();
  const [update] = useUpdate();
  const { record, isLoading } = useEditController();

  const onSubmit = async (data: CommissionsEditFormValues) => {
    const transformedData = transform(data);

    try {
      await update(
        Resources.Commissions,
        {
          id: record?.id,
          data: transformedData,
        },
        { returnPromise: true },
      );

      await queryClient.invalidateQueries({
        queryKey: [[Resources.Commissions], [Resources.Commissions, record?.id]],
      });
      notify(translate('app.commissions.edited', { name: transformedData.name }), {
        type: 'success',
        messageArgs: { smart_count: 1 },
      });

      goBack();
    } catch (error) {
      notify(`Ошибка: ${(error as Error)?.message || 'ошибка сервера'}`, {
        type: 'error',
        autoHideDuration: 3000,
      });

      return getSubmissionErrors(error as HttpError);
    }
  };

  const isEditable =
    !record ||
    [CommissionStatuses.Created, CommissionStatuses.Rejected].includes(
      record.status?.value as CommissionStatuses,
    );

  if (!isLoading && !isEditable) {
    return <NotFound />;
  }

  return (
    <EditPage listName="Комиссии" title="Редактирование комиссии" width="600px">
      <SimpleForm
        onSubmit={onSubmit as any}
        resolver={yupResolver<FieldValues>(COMMISSIONS_SCHEMA)}
        toolbar={false}
      >
        <CommissionsEditForm />
        <CardActionsButtons
          rightActionsSlot={[
            <CancelButton key="cancel-button" variant="outlined" />,
            <SaveButton key="save-button" label="Сохранить" />,
          ]}
          sx={{ marginTop: 'auto', padding: 0 }}
        />
      </SimpleForm>
    </EditPage>
  );
};
